import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Modal, Form, FormProps, notification, DatePicker } from 'antd';
import useModalStore from 'stores/useModal';
import useBookingStore from 'stores/useBooking';

import { ReactComponent as IconClose } from 'assets/images/close.svg';
import { BookingStatus } from 'services/api/type/booking.type';
import { updateBreakfast } from 'services/api/module/booking.api';
import { useMutation } from '@tanstack/react-query';
import { toUtcTime } from 'utils';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import { useTranslation } from 'react-i18next';

function SetBreakfastModal() {
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const { isOpenSetBreakfast, setIsOpenSetBreakfast } = useModalStore();
  const { bookingLineDetail } = useBookingStore();

  const { mutateAsync: mutateUpdateBreakfast, isPending } = useMutation({
    mutationFn: (params: any = []) =>
      updateBreakfast(Number(bookingLineDetail?.booking_line_id), params)
  });

  useEffect(() => {
    if (bookingLineDetail && isOpenSetBreakfast) {
      form.setFieldsValue({
        breakfast_dates: bookingLineDetail.breakfast_dates
          .filter(item => item.active)
          .map(item => dayjs.utc(item.date).local())
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingLineDetail, isOpenSetBreakfast]);

  const handleCloseSetBreakfast = () => {
    setIsOpenSetBreakfast(false);
    form.resetFields();
  };

  const handleSetBreakfast = () => {
    form.submit();
  };

  const startDate = useMemo(() => {
    const dateString = _.get(bookingLineDetail, 'check_in', '');
    const date = dayjs.utc(dateString).local().add(1, 'day');
    return date;
  }, [bookingLineDetail]);

  const endDate = useMemo(() => {
    if (bookingLineDetail) {
      const dateString = _.get(bookingLineDetail, `check_out`, '');
      return dayjs.utc(dateString).local();
    }
    return dayjs();
  }, [bookingLineDetail]);

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    if (bookingLineDetail) {
      try {
        await mutateUpdateBreakfast(
          values?.breakfast_dates?.map((item: any) => {
            return {
              date: toUtcTime(item.set('hour', 23).set('minute', 59), 'YYYY-MM-DD')
            };
          }) || []
        );

        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.KEY_ROOM,
            QUERY_KEYS.GET_BOOKING_DETAIL,
            bookingLineDetail.booking_line_id
          ]
        });

        setIsOpenSetBreakfast(false);
      } catch (err: any) {
        notification.error({
          message: err.error || t('common.error')
        });
      }
    }
  };

  return (
    <Modal
      title={t('managePage.breakfastPage.breakfast')}
      centered
      width={410}
      open={isOpenSetBreakfast}
      onCancel={handleCloseSetBreakfast}
      closeIcon={<IconClose />}
      okText={t('common.actions.save')}
      cancelText={t('common.actions.cancel')}
      onOk={handleSetBreakfast}
      okButtonProps={{ className: 'ant-btn-secondary' }}
      className="modal-set-breakfast"
      confirmLoading={isPending}
      destroyOnClose
    >
      <Form
        form={form}
        name="breakfast-calendar-form"
        layout="vertical"
        style={{ width: '100%' }}
        initialValues={{ breakfast_dates: [] }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <p>{t('managePage.breakfastPage.quote')}</p>

        <Form.Item label={t('managePage.breakfastPage.selectBreakfastDate')} name="breakfast_dates">
          <DatePicker
            multiple
            minDate={startDate}
            maxDate={endDate}
            maxTagCount="responsive"
            format="DD/MM/YYYY"
            className="w-full"
            placeholder={t('managePage.breakfastPage.selectDate')}
            disabled={
              ![BookingStatus.CHECK_IN, BookingStatus.CONFIRM].includes(
                bookingLineDetail?.status as BookingStatus
              )
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default SetBreakfastModal;
